import {
  AnnotationTypeName,
  usePlanDetailSectionQuery
} from 'generated/graphql';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { appFormUtil } from 'modules/common';
import { IPlanMetaDetail } from 'modules/components/layer35/PlanMetaDetailSection/types';
import React, { useEffect } from 'react';



type IFormState = IPlanMetaDetail;

const initialState: IFormState = {
  id: '',
  title: '',
  fileSize: '',
  selectedFileId: '',
  selectedFileUrl: '',
  createdTimestamp: DateTime.now(),
  updatedTimestamp: DateTime.now(),
  versions: [],
  annotations: [],
  tasks: [],
  documents: []
};

export function useFormState(planId: string) {
  const {
    formState,
    dispatch,
    genFieldProps
  } = appFormUtil.useFormState({
    initialState,
    key2FieldConfig: {
      id: {
        type: 'text'
      },
      title: {
        type: 'text'
      },
      fileSize: {
        type: 'text'
      },
      createdTimestamp: {
        type: 'text'
      },
      updatedTimestamp: {
        type: 'text'
      },
      versions: {
        type: 'text'
      }
    }
  });

  const { data, loading, called } = useQuery(planId);

  useEffect(() => {
    if (data && !loading && called && formState.id === '') {
      dispatch({
        type: 'BATCH_UPDATE',
        payload: {
          ...data
        }
      });
    }
  }, [
    called,
    data,
    dispatch,
    formState.id,
    loading
  ]);

  return React.useMemo(
    () => {


      return {
        formState,
        dispatch,
        genFieldProps
      };
    },
    [
      dispatch,
      formState,
      genFieldProps
    ]
  );
}


export type IUseFormState = ReturnType<typeof useFormState>;

export interface IData extends IFormState {}



export function useQuery(id: string) {
  const tuple = usePlanDetailSectionQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  return React.useMemo(
    () => {
      const plan = tuple.data?.plan;
      const data: IData = {
        id: plan?.id ?? '',
        title: plan?.name ?? '',
        fileSize: plan?.versions[0]?.file?.formattedFileSize ?? '',
        createdTimestamp: plan?.createdDate ?? DateTime.now(),
        selectedFileId: plan?.versions[0]?.id ?? '',
        selectedFileUrl: plan?.versions[0]?.file.url ?? '',
        updatedTimestamp: plan?.updatedDate ?? DateTime.now(),
        versions: plan?.versions?.map((node) => ({
          uploadedBy: `${node.uploadedBy.fullName}`,
          id: node?.id,
          apiFileId: node.file?.id,
          fileSize: node?.file?.formattedFileSize ?? '',
          fileType: node?.file?.mimeType ?? '',
          url: node?.file?.url ?? ''
        })) ?? [],
        annotations: plan?.annotations?.map((node) => ({
          id: node.id,
          color: node.color,
          page: node.page,
          points: node.points ?? [],
          relatedId: node.relatedId ?? null,
          type: node.type,
          width: node.width
        })) ?? [],
        tasks: plan?.tasks
          .map((node, i) => ({
            id: node.id ?? i,
            area: node.area?.displayName ?? '',
            category: node.category?.displayName ?? '-',
            dueIn: node.dueDate ? _.round(node.dueDate?.diffNow().as('day'), 0) : 0,
            title: node.name ?? ''
          })) ?? [],
        documents: plan?.documents.map((node, i) => ({
          id: node.id ?? '',
          createdAt: node.createdTimestamp.toFormat('DDDD') ?? DateTime.now().toFormat('DDDD'),
          updatedAt: node.createdTimestamp.toFormat('DDDD') ?? DateTime.now().toFormat('DDDD'),
          createdBy: node.uploadedBy.fullName,
          title: node.title
        })) ?? []
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}

